import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Screen } from '@tempmee/react';
import { BackButton, Column, Text } from '~/commonComponents';
import { useAppSelector, useFillViewportHeight, useLoading } from '~/hooks';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import {
  clearAllReceipts,
  FETCH_JOBS_WITH_TRANSACTIONS,
  fetchJobsWithTransactions,
  fetchReceiptList,
} from '../../../actions';
import { paymentHistoryFilterOptions } from '../../../constants';
import { PostingPaymentHistoryTable, RequestReportPopup, Toolbar } from './components';
import { ReceiptTableV2 } from './components/AllReceiptTable/ReceiptTableV2';
import { getFilterDateRage } from './utils';
import '../../../themes/global.scss';

const LIMIT_VALUE = 25;

type TSortOrder = 'asc' | 'desc';

interface IReceiptSort {
  field: string;
  order: TSortOrder;
}

export default function ReceiptList() {
  const dispatch = useDispatch();
  // Bound the posting/shift table's height to the viewport so it overflows-and-scrolls
  // internally; otherwise its `onScroll` (infinite scroll) never fires (GOTU-4127).
  const {
    setRef: setReceiptsScrollRef,
    containerRef: receiptsScrollRef,
    maxHeight: receiptsMaxHeight,
  } = useFillViewportHeight();

  const isFinalPage = useAppSelector((state) => state.job.isFinalPage);
  const isActiveRequest = useAppSelector((state) => state.job.isActiveRequest);

  const jobsWithTransactionPagination = useAppSelector(
    (state) => state.job.jobsWithTransactionPagination,
  );
  const receiptsPagination = useAppSelector((state) => state.job.receiptsPagination);
  const receiptDisplayMode = useAppSelector((state) => state.job.receiptDisplayMode);
  const [sort, setSort] = useState<IReceiptSort>({
    field: 'date',
    order: 'desc',
  });
  const [filter, setFilter] = useState<string>(paymentHistoryFilterOptions.ALL);
  const [isShowRequestReportPopup, setIsShowRequestReportPopup] = useState(false);

  const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

  const { isLoading: isLoadingJobsWithTransactions } = useLoading([FETCH_JOBS_WITH_TRANSACTIONS]);
  // Synchronous in-flight lock for the posting/shift pagination. `state.job.isActiveRequest`
  // is never set by the jobs-with-transactions fetch, so on its own it can't stop the burst of
  // scroll events fired at the bottom from re-requesting the same page (GOTU-4127).
  const isFetchingJobsRef = useRef(false);

  useEffect(() => {
    // Release the lock once the request settles (success or error).
    if (!isLoadingJobsWithTransactions) {
      isFetchingJobsRef.current = false;
    }
  }, [isLoadingJobsWithTransactions]);

  useEffect(() => {
    // Reset filter when changing display mode
    setFilter(paymentHistoryFilterOptions.ALL);
  }, [receiptDisplayMode]);

  useEffect(() => {
    dispatch(clearAllReceipts());

    if (receiptDisplayMode === 'shift') {
      dispatch(
        fetchJobsWithTransactions({
          page: 1,
          limit: LIMIT_VALUE,
          sort: sort.field,
          order: sort.order,
        }),
      );
    }
  }, [dispatch, receiptDisplayMode, sort, filter]);

  const handleScroll = () => {
    const receiptsScroll = receiptsScrollRef.current;

    if (!receiptsScroll) return;

    const userScrollHeight = receiptsScroll.scrollHeight - receiptsScroll.scrollTop - 20;
    const clientHeight = receiptsScroll.clientHeight;

    if (receiptDisplayMode === 'all') {
      if (userScrollHeight <= clientHeight && !isActiveRequest && !isFinalPage) {
        dispatch(
          fetchReceiptList({
            page: receiptsPagination?.page + 1,
            limit: LIMIT_VALUE,
            sort: sort.field,
            order: sort.order,
            ...getFilterDateRage(filter),
          }),
        );
      }
    } else if (
      userScrollHeight <= clientHeight &&
      !isLoadingJobsWithTransactions &&
      !isFetchingJobsRef.current &&
      !jobsWithTransactionPagination?.isFinal
    ) {
      isFetchingJobsRef.current = true;
      dispatch(
        fetchJobsWithTransactions({
          page: jobsWithTransactionPagination?.page + 1,
          limit: LIMIT_VALUE,
        }),
      );
    }
  };

  const changeSort = (sortName: string) => {
    setSort({
      field: sortName,
      order: sort.order === 'asc' ? 'desc' : 'asc',
    });

    if (receiptsScrollRef.current) {
      receiptsScrollRef.current.scrollTop = 0;
    }
  };

  return (
    <>
      <Screen variant="standard" data-cy="payment-history-container">
        <Screen.Header start={<BackButton />} title="Payment History" />
        <Screen.Body>
          <Column alignItems="center" justifyContent="center">
            <Text fontSize={22}>
              {receiptDisplayMode === 'shift'
                ? `By ${isEnforcePermanentJobV2 ? 'Posting' : 'Shift'}`
                : 'All Transactions'}
            </Text>
          </Column>

          <Toolbar
            displayMode={receiptDisplayMode}
            filter={filter}
            onFilterChange={(val) => setFilter(val)}
            onShowRequestReportPopup={() => setIsShowRequestReportPopup(true)}
          />

          {receiptDisplayMode === 'all' ? (
            <ReceiptTableV2
              filters={{
                transactionDate: filter,
              }}
            />
          ) : (
            <PostingPaymentHistoryTable
              ref={setReceiptsScrollRef}
              maxHeight={receiptsMaxHeight}
              sort={{
                direction: sort.order === 'asc' ? 'ASC' : 'DESC',
                field: sort.field,
              }}
              onScroll={handleScroll}
              onSort={changeSort}
            />
          )}
        </Screen.Body>
      </Screen>
      {isShowRequestReportPopup && (
        <RequestReportPopup onClose={() => setIsShowRequestReportPopup(false)} />
      )}
    </>
  );
}
